import { Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterOutlet } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import {
  GetOrganizationOptions,
  SetCurrentOrganizationId,
  SignOut,
} from '@taxora-accountant-core/data-access/state/app.actions';
import { AppState } from '@taxora-accountant-core/data-access/state/app.state';
import { User } from '@taxora-accountant-core/util/models/user';
import { AccountMenuService } from '@taxora-accountant-core/util/services/account-menu.service';
import { NavigationService } from '@taxora-accountant-core/util/services/navigation.service';
import { MessageService } from 'primeng/api';
import { SelectModule } from 'primeng/select';
import { APP_ROUTES } from '@taxora-accountant-core/util/constants/app-routes.constant';
import { DEFAULT_IDLE_WARNING_BEFORE_TIMEOUT_MS, IdleSession } from '@taxora-shared-core/util';
import { AccountMenu } from '@taxora-shared-common/ui';
import { UserRole } from '@taxora-shared-common/util';
import { Header, Navigation, SplitView } from '@taxora-shared-layout/ui';

@Component({
  selector: 'app-authenticated-layout',
  imports: [
    Header,
    SplitView,
    Navigation,
    RouterOutlet,
    AccountMenu,
    TranslatePipe,
    SelectModule,
    FormsModule,
  ],
  templateUrl: './authenticated-layout.html',
  styleUrl: './authenticated-layout.scss',
})
export class AuthenticatedLayout implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly messageService = inject(MessageService);
  private readonly navigationService = inject(NavigationService);
  private readonly accountMenuService = inject(AccountMenuService);
  private readonly idleSessionService = inject(IdleSession);

  protected readonly navigationMenuItems = this.navigationService.getNavigationMenuItems();
  protected readonly accountMenuItems = this.accountMenuService.getAccountMenuItems();
  protected readonly organizations = this.store.selectSignal(AppState.organizationOptions);
  protected readonly selectedOrganizationId = this.store.selectSignal(
    AppState.currentOrganizationId,
  );
  protected readonly isSidebarCollapsed = signal(false);
  protected readonly sidebarToggleTooltip = computed(() =>
    this.translate.instant(
      this.isSidebarCollapsed() ? 'app.navigation.expandSidebar' : 'app.navigation.collapseSidebar',
    ),
  );
  protected userModel?: User;

  constructor() {
    const user = this.store.selectSnapshot(AppState.user);
    if (user) {
      this.userModel = new User(user.firstName, user.lastName, user.role as UserRole);
    }
  }

  ngOnInit(): void {
    this.store.dispatch(new GetOrganizationOptions());
    this.accountMenuService.setMenuItemCommand('profile', () => this.openProfile());
    this.accountMenuService.setMenuItemCommand('signOut', () => this.logout());
    this.idleSessionService.start({
      onTimeout: () => this.logout(),
      onWarning: () => this.showIdleWarning(),
      onActivity: () => this.messageService.clear(),
    });
  }

  ngOnDestroy(): void {
    this.messageService.clear();
    this.idleSessionService.stop();
  }

  protected onOrganizationChange(organizationId: number): void {
    this.store.dispatch(new SetCurrentOrganizationId(organizationId));
  }

  protected onToggleChange(isCollapsed: boolean): void {
    this.isSidebarCollapsed.set(isCollapsed);
  }

  private logout(): void {
    this.messageService.clear();
    this.store.dispatch(new SignOut());
  }

  private openProfile(): void {
    void this.router.navigate(['/', APP_ROUTES.PROFILE]);
  }

  private showIdleWarning(): void {
    this.messageService.clear();
    this.messageService.add({
      severity: 'warn',
      summary: this.translate.instant('app.session.idleWarningTitle'),
      detail: this.translate.instant('app.session.idleWarningMessage', {
        minutes: Math.round(DEFAULT_IDLE_WARNING_BEFORE_TIMEOUT_MS / 60000),
      }),
      life: DEFAULT_IDLE_WARNING_BEFORE_TIMEOUT_MS,
    });
  }
}
